import { useLanguage } from "../i18n/LanguageContext";
import { ArrowLeft, ArrowRight, BookOpen, Clock, Calendar } from "lucide-react";
import { Link } from "react-router-dom";
import { SEO } from "../components/SEO";

const articles = [
  {
    id: 'natural-deodorant',
    slug: '/articles/natural-deodorant',
    title: {
      he: 'על דאודורנט טבעי',
      en: 'About Natural Deodorant'
    },
    subtitle: {
      he: 'ידע פתוח לקוסמטיקה מודעת',
      en: 'Open knowledge for conscious cosmetics'
    },
    excerpt: {
      he: 'בעשור האחרון יותר ויותר אנשים מחפשים דאודורנט טבעי, כזה שעובד באמת, מכיל רכיבים טבעיים, ואינו מבוסס על כימיקלים תעשייתיים אגרסיביים.',
      en: 'In the last decade, more and more people are looking for natural deodorant, one that actually works, contains natural ingredients, and is not based on aggressive industrial chemicals.'
    },
    readTime: { he: '5 דקות קריאה', en: '5 min read' },
    category: { he: 'מאמר מערכת', en: 'Editorial' },
    image: '/images/mini-tins.jpg'
  },
  {
    id: 'sodium-bicarbonate',
    slug: '/articles/sodium-bicarbonate',
    title: {
      he: 'סודיום ביקרבונט: המדע שמאחורי נטרול ריח גוף באופן טבעי',
      en: 'Sodium Bicarbonate: The Science Behind Natural Body Odor Neutralization'
    },
    subtitle: {
      he: 'מנגנון פעולה, איכות חומר גלם והיבטים פסיכולוגיים',
      en: 'Mechanism of action, raw material quality and psychological aspects'
    },
    excerpt: {
      he: 'בעולם הטיפוח האישי, החיפוש אחר חלופות נקיות מאלומיניום ופרבנים מוביל חזרה לרכיב בסיסי ומוכח: סודיום ביקרבונט.',
      en: 'In the world of personal care, the search for clean alternatives to aluminum and parabens leads back to a basic and proven ingredient: sodium bicarbonate.'
    },
    readTime: { he: '4 דקות קריאה', en: '4 min read' },
    category: { he: 'מדע ובריאות', en: 'Science & Health' },
    image: '/images/sodium-bicarbonate.jpg'
  }
];

export const Articles = () => {
  const { language } = useLanguage();
  const Arrow = language === 'he' ? ArrowLeft : ArrowRight;
  const isRTL = language === 'he';

  return (
    <div className="min-h-screen pt-20 pb-12 bg-[#FAF8F5] dark:bg-[#0f1510]">
      <SEO 
        page="articles"
        title={isRTL ? "מאמרים | M'art - דאודורנט טבעי" : "Articles | M'art - Natural Deodorant"}
        description={isRTL ? "מאמרים על דאודורנט טבעי, קוסמטיקה מודעת והמדע שמאחורי הרכיבים הטבעיים" : "Articles about natural deodorant, conscious cosmetics and the science behind natural ingredients"}
      />
      
      {/* Header */}
      <section className="px-4 sm:px-6 mb-10">
        <div className="max-w-4xl mx-auto text-center">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#4A5D43]/10 dark:bg-[#4A5D43]/20 text-[#4A5D43] dark:text-[#9ACD8A] text-xs font-medium rounded-full mb-4 tracking-wider uppercase">
            <BookOpen className="h-3.5 w-3.5" />
            {isRTL ? 'מאמרים' : 'Articles'}
          </span>
          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium text-[#2C3329] dark:text-white mb-4">
            {isRTL ? 'ידע פתוח' : 'Open Knowledge'}
          </h1>
          <p className="text-base sm:text-lg text-[#6B7266] dark:text-[#9A9790] max-w-2xl mx-auto">
            {isRTL 
              ? 'מאמרים מקיפים על דאודורנט טבעי, המדע שמאחורי הרכיבים וקוסמטיקה מודעת'
              : 'Comprehensive articles about natural deodorant, the science behind ingredients and conscious cosmetics'
            }
          </p>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <div className="grid gap-8">
            {articles.map((article, index) => (
              <Link 
                key={article.id}
                to={article.slug}
                className="group"
              >
                <article className="bg-white dark:bg-[#1a231a] rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-[#E8E4DC] dark:border-[#2a3a2a]">
                  <div className={`flex flex-col ${index % 2 === 1 ? 'md:flex-row-reverse' : 'md:flex-row'}`}>
                    {/* Image */}
                    <div className="md:w-2/5 h-48 md:h-auto relative overflow-hidden">
                      <img 
                        src={article.image} 
                        alt={article.title[language]}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent md:hidden" />
                    </div>
                    
                    {/* Content */}
                    <div className="md:w-3/5 p-6 md:p-8 flex flex-col justify-center">
                      <div className="flex items-center gap-3 mb-3">
                        <span className="px-3 py-1 bg-[#4A5D43]/10 dark:bg-[#4A5D43]/20 text-[#4A5D43] dark:text-[#9ACD8A] text-xs font-medium rounded-full">
                          {article.category[language]}
                        </span>
                        <span className="flex items-center gap-1 text-xs text-[#9A9790]">
                          <Clock className="h-3 w-3" />
                          {article.readTime[language]}
                        </span>
                      </div>
                      
                      <h2 className="font-serif text-xl md:text-2xl font-medium text-[#2C3329] dark:text-white mb-2 group-hover:text-[#4A5D43] dark:group-hover:text-[#9ACD8A] transition-colors">
                        {article.title[language]}
                      </h2>
                      
                      <p className="text-sm text-[#6B7266] dark:text-[#9A9790] mb-1">
                        {article.subtitle[language]}
                      </p>
                      
                      <p className="text-[#6B7266] dark:text-[#9A9790] text-sm leading-relaxed mb-4 line-clamp-2">
                        {article.excerpt[language]}
                      </p>
                      
                      <div className="flex items-center gap-2 text-[#4A5D43] dark:text-[#9ACD8A] font-medium text-sm group-hover:gap-3 transition-all">
                        {isRTL ? 'קראו את המאמר המלא' : 'Read full article'}
                        <Arrow className="h-4 w-4" />
                      </div>
                    </div>
                  </div>
                </article>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="px-4 sm:px-6 mt-16">
        <div className="max-w-2xl mx-auto text-center py-10 px-6 bg-gradient-to-b from-[#F5F2EB] to-[#FAF8F5] dark:from-[#1a231a] dark:to-[#0f1510] rounded-2xl border border-[#E8E4DC] dark:border-[#2a3a2a]">
          <p className="font-serif text-2xl text-[#4A5D43] dark:text-[#9ACD8A] font-medium mb-4">
            {isRTL ? 'הידע נשאר פתוח.' : 'The knowledge remains open.'}
          </p>
          <p className="text-[#6B7266] dark:text-[#9A9790] mb-6">
            {isRTL 
              ? 'לצפייה בפורמולה המלאה והכנה עצמית'
              : 'View the full formula and make it yourself'
            }
          </p>
          <Link 
            to="/diy"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#4A5D43] hover:bg-[#3A4A35] text-white rounded-full font-medium transition-colors"
          >
            {isRTL ? 'לפורמולה המלאה' : 'View Full Formula'}
            <Arrow className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Articles;
